import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, KeyboardAvoidingView, Platform, Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MaterialCommunityIcons } from '../components/icons';
import { Check, FileText, Link2, SlidersHorizontal, Upload } from '../components/icons';
import { GradientButton } from '../components/GradientButton';
import { Button } from '../components/Button';
import { CarteScore } from '../components/CarteScore';
import { PiedAction } from '../components/PiedAction';
import { SectionRepliable } from '../components/SectionRepliable';
import { Segmented } from '../components/Segmented';
import { ScreenHeader } from '../components/ScreenHeader';
import { analyseCoverLetter, type CvOfferInput } from '../services/claude';
import { lireDocumentImporte, pickDocumentFile, type PickedFile } from '../services/mediaPicker';
import { listSavedCvs, type SavedCv } from '../services/cv';
import { getCoverLetterAnalysis, listCoverLetters, saveCoverLetter } from '../services/coverLetter';
import { lettreDepuisTexte, texteDeLettre } from '../utils/letterText';
import { diagnosticPourAmelioration } from '../utils/ameliorationLettre';
import { formatSavedDate } from '../utils/cvDates';
import { LETTER_PROFILS, type LetterProfil } from '../types';
import { useLetterDefaults } from '../hooks/useLetterDefaults';
import { useGabarit } from '../hooks/useGabarit';
import type { LetterAnalysis, LetterImproveDiagnostic, SavedCoverLetter } from '../types';
import { t, tBrut } from '../i18n';
import { colors, formes, layout, radius, screenPadding, shadow, spacing, typography } from '../theme/tokens';
import { FEATURE_ACCENTS } from '../theme/featureAccents';

/**
 * Analyse et score d'une lettre de motivation — lot 2.
 *
 * DEUX ENTRÉES : une lettre déjà enregistrée, ou une lettre collée. Dans le second cas la lettre
 * est ENREGISTRÉE avant d'être analysée, comme l'import d'un CV remplit « Mes CV » : sans ligne en
 * base, il n'y aurait rien à quoi rattacher le diagnostic, et le § 6 de la politique promet qu'il
 * est conservé avec la lettre à laquelle il se rapporte.
 *
 * Le score affiché n'est PAS annoncé par le modèle : il est reconstruit côté serveur à partir des
 * sous-notes, des pondérations du profil et des plafonds. Rien n'est recalculé ici.
 */
/** Ce que l'écran d'amélioration reçoit : la lettre analysée et de quoi orienter les modifications. */
export interface AmeliorationLettreParams {
  letter: SavedCoverLetter;
  /** Absent quand l'analyse porte sur une version antérieure du texte : ses extraits ne correspondraient plus. */
  diagnostic: LetterImproveDiagnostic | undefined;
  annonce: string;
  /** CV principal sérialisé, vide si la personne n'en a pas. */
  cv: string;
  profil: LetterProfil;
}

export interface CoverLetterAnalyseScreenProps {
  onBack?: () => void;
  /** Lettre pré-sélectionnée quand on arrive depuis « Mes lettres ». */
  letter?: SavedCoverLetter;
  /** Ouvre « Améliorer ma lettre » ; sans lui, le bouton n'est pas proposé. */
  onImprove?: (params: AmeliorationLettreParams) => void;
}

type Etape = 'saisie' | 'chargement' | 'resultat';
type Source = 'saved' | 'import' | 'paste';

const ACCENT = FEATURE_ACCENTS.letter;

// Titre de la lettre quand la personne n'en a pas saisi : le nom du fichier, sans extension, ou la
// date du collage. Le titre n'est plus demandé, mais « Mes lettres » doit rester lisible.
const titreParDefaut = (saisi: string, nomFichier: string | null): string => {
  if (saisi) return saisi;
  if (nomFichier) return nomFichier.replace(/\.[^.]+$/, '');
  // `tBrut` : ce titre est ENREGISTRÉ en base, il ne doit pas porter les marques de direction que
  // `t` ajoute pour l'affichage en arabe.
  return tBrut('letterAnalyse.pastedTitle', { date: formatSavedDate(new Date().toISOString()) });
};

const GRAVITE_TON: Record<string, string> = {
  majeure: colors.error,
  moyenne: colors.warning,
  mineure: colors.grey500,
};

function ChipRow<T extends string>({
  label,
  hint,
  options,
  value,
  onChange,
  testID,
}: {
  label: string;
  hint?: string;
  options: Array<{ key: T; label: string }>;
  value: T;
  onChange: (v: T) => void;
  testID: string;
}) {
  return (
    <View style={styles.field}>
      <Text style={styles.fieldLabel}>{label}</Text>
      {hint ? <Text style={styles.fieldHint}>{hint}</Text> : null}
      <View style={styles.chips}>
        {options.map((o) => (
          <Pressable
            key={o.key}
            testID={`${testID}-${o.key}`}
            accessibilityRole="button"
            accessibilityState={{ selected: o.key === value }}
            onPress={() => onChange(o.key)}
            style={[styles.chip, o.key === value && styles.chipActive]}
          >
            <Text style={[styles.chipText, o.key === value && styles.chipTextActive]}>{o.label}</Text>
          </Pressable>
        ))}
      </View>
    </View>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View style={styles.section}>
      <Text accessibilityRole="header" style={styles.sectionTitle}>
        {title}
      </Text>
      {children}
    </View>
  );
}

export function CoverLetterAnalyseScreen({ onBack, letter, onImprove }: CoverLetterAnalyseScreenProps) {
  const { large } = useGabarit();
  const [etape, setEtape] = useState<Etape>('saisie');
  const [erreur, setErreur] = useState<string | null>(null);
  const [analyse, setAnalyse] = useState<LetterAnalysis | null>(null);
  const [obsolete, setObsolete] = useState(false);
  // La lettre (enregistrée) que le résultat affiché concerne : c'est elle qu'on propose d'améliorer.
  const [lettreAnalysee, setLettreAnalysee] = useState<SavedCoverLetter | null>(null);

  const [saved, setSaved] = useState<SavedCoverLetter[]>([]);
  const [choisie, setChoisie] = useState<SavedCoverLetter | null>(letter ?? null);
  const [colle, setColle] = useState('');
  // L'import S'AJOUTE au collage, il ne le remplace pas : un seul mode actif à la fois, comme
  // `buildOffer` de CvScoreScreen. Le collage reste le seul chemin où le texte est exact par
  // construction — un PDF passe par une transcription du modèle.
  const [fichier, setFichier] = useState<PickedFile | null>(null);
  const [titre, setTitre] = useState('');
  const [annonce, setAnnonce] = useState('');
  const [metier, setMetier] = useState('');
  const [profil, setProfil] = useState<LetterProfil>('etudiant_jeune_diplome');
  // Profil retenu dans le compte : préremplit le choix, sans jamais écraser un choix déjà fait.
  const { defauts, memoriser } = useLetterDefaults();
  const profilTouche = useRef(false);
  useEffect(() => {
    if (defauts?.profil && !profilTouche.current) setProfil(defauts.profil);
  }, [defauts]);
  const profilRepris = !!defauts?.profil && defauts.profil === profil;
  // Source choisie par la personne ; tant qu'elle n'a rien touché, « Mes lettres » s'il y en a,
  // « Coller » sinon. C'est l'onglet actif qui décide de ce qui part : les saisies des autres
  // onglets sont gardées mais jamais envoyées.
  const [source, setSource] = useState<Source | null>(letter ? 'saved' : null);
  const [annonceOuverte, setAnnonceOuverte] = useState(false);
  const [profilOuvert, setProfilOuvert] = useState(false);
  const [cvs, setCvs] = useState<SavedCv[]>([]);
  // Conservé entre deux tentatives : sans lui, chaque relance après un refus de quota créerait une
  // nouvelle lettre dans « Mes lettres ».
  // Lié à la SOURCE qui l'a produit (voir `signature`) : une relance sur le même texte ou le même
  // fichier réutilise la lettre déjà enregistrée, un autre texte en crée une nouvelle.
  const [brouillon, setBrouillon] = useState<{ signature: string; id: string } | null>(null);

  useEffect(() => {
    listCoverLetters().then(setSaved).catch(() => setSaved([]));
    listSavedCvs().then(setCvs).catch(() => setCvs([]));
  }, []);

  // Analyse déjà produite pour cette lettre : on la relit plutôt que d'en refacturer une. Si le
  // texte a changé depuis, elle est affichée mais signalée comme portant sur la version précédente.
  // Nombre d'analyses lancées : une analyse stockée qui arrive après un lancement n'a plus rien à dire.
  const lancements = useRef(0);

  useEffect(() => {
    if (!choisie) return;
    // Périmée si la lettre change (ou est décochée) avant la réponse, ou si une analyse est lancée
    // entre-temps : la saisie est plein écran, un saut vers un autre résultat se verrait.
    let perimee = false;
    const depart = lancements.current;
    getCoverLetterAnalysis(choisie.id)
      .then((stored) => {
        if (perimee || depart !== lancements.current || !stored) return;
        setAnalyse(stored.diagnostic);
        setObsolete(stored.empreinte !== stored.diagnostic.empreinte);
        setLettreAnalysee(choisie);
        setEtape('resultat');
      })
      .catch(() => undefined);
    return () => {
      perimee = true;
    };
  }, [choisie]);

  // Le CV PRINCIPAL, sinon le plus récent. Sans aucun CV, `cv` part vide : le critère est alors
  // NON ÉVALUÉ, jamais noté zéro — une lettre ne doit rien perdre à l'absence de CV.
  const cvPrincipal = useMemo(() => cvs.find((c) => c.is_primary) ?? cvs[0] ?? null, [cvs]);
  const sourceActive: Source = source ?? (saved.length > 0 ? 'saved' : 'paste');
  const choisieActive = sourceActive === 'saved' ? choisie : null;
  const fichierActif = sourceActive === 'import' ? fichier : null;
  const texte = choisieActive ? texteDeLettre(choisieActive.content) : sourceActive === 'paste' ? colle.trim() : '';
  const aUneSource = sourceActive === 'import' ? fichierActif !== null : texte.length > 0;
  const pretALancer = aUneSource;

  // Identité de ce qui va partir : change quand le texte collé, le fichier ou la lettre choisie change.
  const signature =
    sourceActive === 'saved'
      ? `saved:${choisieActive?.id ?? ''}`
      : sourceActive === 'import'
        ? `file:${fichierActif?.uri ?? ''}`
        : `paste:${colle}`;

  const changerSource = (s: Source) => {
    setSource(s);
    setErreur(null);
  };

  // L'onglet par défaut dépend de la liste des lettres, qui arrive après l'ouverture : dès que la
  // personne saisit quelque chose, on le fige pour qu'il ne bascule pas sous ses doigts.
  const figerSource = () => {
    if (source === null) setSource(sourceActive);
  };

  /**
   * SEUL DES HUIT CHEMINS À N'AVOIR JAMAIS ÉTÉ MUET — et c'était son autre défaut : appelé depuis
   * le `try` de `lancer`, qui affiche `e.message`, une lecture ratée montrait le texte technique
   * anglais de l'exception. Le message vient désormais de la frontière, en français et actionnable.
   */
  const buildLettre = async (): Promise<CvOfferInput> => {
    if (fichierActif) {
      const { payload, error } = await lireDocumentImporte(fichierActif);
      if (!payload) throw new Error(error);
      return payload;
    }
    return { text: texte };
  };

  const choisirFichier = async () => {
    const outcome = await pickDocumentFile();
    if (outcome.error) return setErreur(outcome.error);
    if (!outcome.file) return;
    setErreur(null);
    setFichier(outcome.file);
  };

  const lancer = useCallback(async () => {
    lancements.current += 1;
    setErreur(null);
    setLettreAnalysee(null);
    setEtape('chargement');
    try {
      let coverLetterId: string | null = choisieActive ? choisieActive.id : brouillon?.signature === signature ? brouillon.id : null;
      const titreFinal = titreParDefaut(titre.trim(), fichierActif?.name ?? null);

      // Lettre COLLÉE : on l'enregistre avant d'analyser, le texte étant déjà connu. Si l'analyse
      // échoue ensuite, la lettre reste — c'est le texte de la personne, le perdre serait pire.
      // Lettre IMPORTÉE : impossible ici, le client n'a pas le texte. Elle est enregistrée APRÈS
      // l'appel, à partir de `lettre_texte` que le serveur renvoie — même motif que le mode
      // `extract` de claude-cv, qui renvoie le CV structuré que l'écran persiste.
      if (!choisieActive && !coverLetterId && !fichierActif) {
        coverLetterId = await saveCoverLetter(lettreDepuisTexte(colle, titreFinal), titreFinal);
        setBrouillon({ signature, id: coverLetterId });
      }

      const resultat = await analyseCoverLetter({
        lettre: await buildLettre(),
        annonce: { text: annonce.trim() },
        cv: cvPrincipal ? JSON.stringify(cvPrincipal.content) : '',
        profil,
        langue: choisieActive?.output_language ?? 'fr',
        metier: metier.trim(),
        organisation: '',
        typeContrat: '',
        coverLetterId: coverLetterId ?? undefined,
      });

      // Lettre importée : la ligne n'existe pas encore, on la crée depuis le texte renvoyé.
      if (!choisieActive && !coverLetterId && resultat.lettre_texte) {
        coverLetterId = await saveCoverLetter(
          lettreDepuisTexte(resultat.lettre_texte, titreFinal, resultat.texte_reconstitue),
          titreFinal,
        );
        setBrouillon({ signature, id: coverLetterId });
      }

      // La lettre analysée telle qu'elle est enregistrée, pour pouvoir l'améliorer ensuite. Une lettre
      // collée se reconstruit du texte collé, une lettre importée du texte renvoyé par le serveur.
      let analysee: SavedCoverLetter | null = choisieActive;
      if (!analysee && coverLetterId) {
        const contenu = fichierActif
          ? resultat.lettre_texte
            ? lettreDepuisTexte(resultat.lettre_texte, titreFinal, resultat.texte_reconstitue)
            : null
          : lettreDepuisTexte(colle, titreFinal);
        if (contenu) {
          analysee = { id: coverLetterId, title: titreFinal, created_at: new Date().toISOString(), content: contenu, output_language: 'fr' };
        }
      }
      setLettreAnalysee(analysee);
      setAnalyse(resultat);
      setObsolete(false);
      setEtape('resultat');
      // Seulement si la personne l'a choisi : voir CoverLetterCreateScreen.
      if (profilTouche.current) memoriser({ profil });
    } catch (e) {
      setErreur(e instanceof Error ? e.message : t('letterAnalyse.failed'));
      setEtape('saisie');
    }
  }, [annonce, brouillon, choisieActive, colle, cvPrincipal, fichierActif, memoriser, metier, profil, signature, texte, titre]);

  // ───────────── Saisie (carte 21) ─────────────
  if (etape === 'saisie') {
    const infoCv = cvPrincipal ? t('letterAnalyse.cvCrossed') : t('letterAnalyse.cvCrossedNone');
  const sSource = (
    <>
            <Text style={styles.intro}>{t('letterAnalyse.subtitle')}</Text>

            <Segmented
              testID="letter-analyse-tabs"
              accessibilityLabel={t('letterAnalyse.sourceSection')}
              value={sourceActive}
              onChange={(k) => changerSource(k as Source)}
              options={[
                { key: 'saved', label: t('letterAnalyse.tabSaved'), testID: 'letter-analyse-tab-saved' },
                { key: 'import', label: t('letterAnalyse.tabImport'), testID: 'letter-analyse-tab-import' },
                { key: 'paste', label: t('letterAnalyse.tabPaste'), testID: 'letter-analyse-tab-paste' },
              ]}
            />

            {sourceActive === 'saved' ? (
              saved.length > 0 ? (
                <View style={styles.liste} testID="letter-analyse-saved" accessibilityRole="radiogroup">
                  {saved.map((l) => {
                    const actif = choisie?.id === l.id;
                    const organisation = l.content?.destinataire?.organisation?.trim();
                    const sousTitre = [organisation, formatSavedDate(l.created_at)].filter(Boolean).join(' · ');
                    return (
                      <Pressable
                        key={l.id}
                        testID={`letter-analyse-saved-${l.id}`}
                        accessibilityRole="radio"
                        accessibilityState={{ checked: actif }}
                        onPress={() => setChoisie(actif ? null : l)}
                        style={[styles.ligneLettre, actif && styles.ligneLettreActive]}
                      >
                        <View style={styles.vignette}>
                          <FileText size={16} color={ACCENT.ink} strokeWidth={2} />
                        </View>
                        <View style={styles.ligneTextes}>
                          <Text style={styles.ligneTitre} numberOfLines={1}>
                            {l.title}
                          </Text>
                          {sousTitre ? <Text style={styles.ligneSous}>{sousTitre}</Text> : null}
                        </View>
                        <View style={[styles.radio, actif && styles.radioOn]}>
                          {actif ? <Check size={12} color={colors.white} strokeWidth={3} /> : null}
                        </View>
                      </Pressable>
                    );
                  })}
                </View>
              ) : (
                <Text style={styles.fieldHint}>{t('letterAnalyse.pickSavedEmpty')}</Text>
              )
            ) : null}

            {sourceActive === 'import' ? (
              <View style={styles.bloc}>
                <Pressable
                  testID="letter-analyse-file"
                  accessibilityRole="button"
                  onPress={() => void choisirFichier()}
                  style={styles.depot}
                >
                  <View style={styles.depotIcone}>
                    <Upload size={22} color={ACCENT.ink} strokeWidth={2.2} />
                  </View>
                  <Text style={styles.depotTitre}>{t('letterAnalyse.importTitle')}</Text>
                  <Text style={styles.depotSous}>{t('letterAnalyse.importHint')}</Text>
                </Pressable>

                {fichier ? (
                  <View style={styles.importPicked} testID="letter-analyse-file-picked">
                    <Text style={styles.importPickedText}>{t('letterAnalyse.importPicked', { name: fichier.name })}</Text>
                    <Pressable
                      testID="letter-analyse-file-remove"
                      accessibilityRole="button"
                      onPress={() => setFichier(null)}
                      hitSlop={8}
                    >
                      <Text style={styles.importRemove}>{t('letterAnalyse.importRemove')}</Text>
                    </Pressable>
                  </View>
                ) : null}
              </View>
            ) : null}

            {sourceActive === 'paste' ? (
              <View style={styles.bloc}>
                <View style={styles.champCarte}>
                  <Text style={styles.fieldLabel}>{t('letterAnalyse.pasteLabel')}</Text>
                  <TextInput
                    testID="letter-analyse-paste"
                    style={[styles.saisie, styles.saisieLongue]}
                    value={colle}
                    onChangeText={(v) => {
                      figerSource();
                      setColle(v);
                    }}
                    placeholder={t('letterAnalyse.pastePlaceholder')}
                    placeholderTextColor={colors.grey500}
                    multiline
                  />
                </View>
                <Text style={styles.fieldHint}>{t('letterAnalyse.pasteSaved')}</Text>
              </View>
            ) : null}

                </>
  );
  const sChamps = (
    <>
{/* Une lettre déjà enregistrée garde son titre ; les deux autres sources en reçoivent un. */}
            {sourceActive !== 'saved' ? (
              <View style={styles.champCarte}>
                <Text style={styles.fieldLabel}>{t('letterAnalyse.titleLabel')}</Text>
                <TextInput
                  testID="letter-analyse-title"
                  style={styles.saisie}
                  value={titre}
                  onChangeText={(v) => {
                    figerSource();
                    setTitre(v);
                  }}
                  placeholder={t('letterAnalyse.titlePlaceholder')}
                  placeholderTextColor={colors.grey500}
                />
              </View>
            ) : null}

            <View style={styles.champCarte}>
              <Text style={styles.fieldLabel}>{t('letterAnalyse.metierLabel')}</Text>
              <TextInput
                testID="letter-analyse-metier"
                style={styles.saisie}
                value={metier}
                onChangeText={setMetier}
                placeholder={t('letterAnalyse.metierPlaceholder')}
                placeholderTextColor={colors.grey500}
              />
            </View>

                </>
  );
  const sOffre = (
    <>
<SectionRepliable
              testID="letter-analyse-section-offer"
              titre={t('letterAnalyse.offerLabel')}
              sousTitre={annonce.trim() ? annonce.trim() : t('letterAnalyse.offerSub')}
              icone={<Link2 size={18} color={colors.electricDeep} strokeWidth={2.2} />}
              indicateur="plus"
              ouverte={annonceOuverte}
              onToggle={() => setAnnonceOuverte((v) => !v)}
            >
              <Text style={styles.fieldHint}>{t('letterAnalyse.offerHint')}</Text>
              <TextInput
                testID="letter-analyse-offer"
                style={[styles.saisie, styles.saisieLongue]}
                value={annonce}
                onChangeText={setAnnonce}
                placeholder={t('letterAnalyse.offerPlaceholder')}
                placeholderTextColor={colors.grey500}
                multiline
              />
            </SectionRepliable>

                </>
  );
  const sProfil = (
    <>
{/* Le profil pèse dans la notation : il ne disparaît pas, il se range ici. */}
            <SectionRepliable
              testID="letter-analyse-section-profil"
              titre={t('letterAnalyse.optionsTitre')}
              sousTitre={t('letterAnalyse.optionsSousTitre')}
              icone={<SlidersHorizontal size={18} color={colors.electricDeep} strokeWidth={2.2} />}
              indicateur="plus"
              ouverte={profilOuvert}
              onToggle={() => setProfilOuvert((v) => !v)}
            >
              <ChipRow
                label={t('letterAnalyse.profileLabel')}
                hint={t('letterAnalyse.profileHint')}
                options={LETTER_PROFILS.map((k) => ({ key: k, label: t(`letterCreate.profils.${k}`) }))}
                value={profil}
                onChange={(k) => {
                  profilTouche.current = true;
                  setProfil(k);
                }}
                testID="letter-analyse-profil"
              />
              {profilRepris ? (
                <Text style={styles.infoCv} testID="letter-analyse-profil-repris">
                  {t('letterAnalyse.profilRepris')}
                </Text>
              ) : null}
            </SectionRepliable>

                </>
  );
  const sInfo = (
    <>
<Text testID="letter-analyse-cv-info" style={styles.infoCv}>
              {infoCv}
            </Text>
    </>
  );
  const sPied = (
    <>
        {/* Un échec s'affiche ICI, à côté du bouton qui l'a provoqué. */}
        <PiedAction testID="letter-analyse-actions" enColonne={large}>
          {erreur ? (
            <Text style={styles.erreurPied} testID="letter-analyse-error">
              {erreur}
            </Text>
          ) : null}
          <GradientButton
            variante="cta"
            hauteur="lg"
            label={t('letterAnalyse.submit')}
            onPress={() => void lancer()}
            disabled={!pretALancer}
            withArrow
            testID="letter-analyse-submit"
          />
        </PiedAction>
    </>
  );

    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        <View testID="letter-analyse-header">
          <ScreenHeader variante="carte" onBack={onBack} testID="letter-analyse-back" title={t('letterAnalyse.title')} />
        </View>

        <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          {/* Ordinateur : la lettre à analyser à gauche, les précisions et le lancement à droite. */}
          {large ? (
            <ScrollView
              testID="letter-analyse-colonnes"
              contentContainerStyle={styles.colonnesAnalyse}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={false}
            >
              <View style={styles.colGaucheAn}>{sSource}</View>
              <View style={styles.colGaucheAn}>
                {sChamps}
                {sOffre}
                {sProfil}
                {sInfo}
                <View style={styles.piedAn}>{sPied}</View>
              </View>
            </ScrollView>
          ) : (
          <ScrollView
            contentContainerStyle={styles.contenuSaisie}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode="on-drag"
            showsVerticalScrollIndicator={false}
          >
            {sSource}
            {sChamps}
            {sOffre}
            {sProfil}
            {sInfo}
          </ScrollView>
          )}
        </KeyboardAvoidingView>

        {large ? null : sPied}
      </SafeAreaView>
    );
  }

  // Pendant l'analyse : la saisie disparaît, l'attente est dite en une phrase.
  if (etape === 'chargement') {
    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        <View testID="letter-analyse-header">
          <ScreenHeader variante="carte" onBack={onBack} testID="letter-analyse-back" title={t('letterAnalyse.resultTitre')} />
        </View>
        <View style={styles.attente}>
          <ActivityIndicator color={ACCENT.ink} />
          <Text style={styles.loading} testID="letter-analyse-loading">
            {t('letterAnalyse.loading')}
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  // Carte 14 : le résultat PREND LA PLACE de la saisie. Le score n'est pas annoncé par le modèle, il
  // est reconstruit côté serveur : rien n'est recalculé ici, le verdict est une étiquette posée sur
  // le palier que le serveur a déjà fixé.
  if (!analyse) return null;
  const rTete = (
    <>
          {obsolete ? (
            <View style={styles.warning} testID="letter-analyse-obsolete">
              <MaterialCommunityIcons name="alert-outline" size={18} color={colors.warning} />
              <Text style={styles.warningText}>{t('letterAnalyse.obsolete')}</Text>
            </View>
          ) : null}

          <CarteScore
            testID="letter-analyse-carte"
            anneauTestID="letter-analyse-ring"
            titreTestID="letter-analyse-verdict"
            detailTestID="letter-analyse-resume"
            score={analyse.score}
            titre={t(`letterAnalyse.verdicts.${analyse.verdict}`)}
            detail={analyse.resume}
          />

          {/* Sans CV, aucune affirmation n'est vérifiée : le dire évite de laisser croire à un
              contrôle de véracité qui n'a pas eu lieu. */}
          {analyse.texte_reconstitue ? (
            <Text style={styles.cvNote} testID="letter-analyse-rebuilt">
              {t('letterAnalyse.rebuiltFromPdf')}
            </Text>
          ) : null}

          <Text style={styles.cvNote} testID="letter-analyse-cv-note">
            {t(analyse.cv_croise ? 'letterAnalyse.cvCrossed' : 'letterAnalyse.cvNotCrossed')}
          </Text>

              </>
  );
  const rPrior = (
    <>
{analyse.priorites.length > 0 ? (
            <Section title={t('letterAnalyse.prioritiesSection')}>
              {analyse.priorites.map((p, i) => (
                <View key={i} testID={`letter-analyse-priorite-${i}`} style={styles.priorite}>
                  <View testID={`letter-analyse-priorite-${i}-num`} style={styles.numero}>
                    <Text style={styles.numeroTexte}>{i + 1}</Text>
                  </View>
                  <Text style={styles.prioriteTexte}>{p}</Text>
                </View>
              ))}
            </Section>
          ) : null}

              </>
  );
  const rForts = (
    <>
{analyse.points_forts.length > 0 ? (
            <Section title={t('letterAnalyse.strengthsSection')}>
              {analyse.points_forts.map((p, i) => (
                <View key={i} testID={`letter-analyse-fort-${i}`} style={styles.fort}>
                  <View style={styles.pastilleFort}>
                    <Check size={14} color={colors.white} strokeWidth={3} />
                  </View>
                  <Text style={styles.prioriteTexte}>{p}</Text>
                </View>
              ))}
            </Section>
          ) : null}

              </>
  );
  const rDetail = (
    <>
<Section title={t('letterAnalyse.detailSection')}>
            {analyse.sous_scores.map((s) => (
              <View key={s.critere} testID={`letter-analyse-critere-${s.critere}`} style={styles.critere}>
                <View style={styles.critereHead}>
                  <Text style={styles.critereName}>{t(`letterAnalyse.criteres.${s.critere}`)}</Text>
                  <Text style={styles.critereNiveau}>{t(`letterAnalyse.niveaux.${s.niveau}`)}</Text>
                </View>
                <Text style={styles.critereJustif}>{s.justification}</Text>
              </View>
            ))}
          </Section>

              </>
  );
  const rErreurs = (
    <>
{analyse.erreurs_detectees.length > 0 ? (
            <Section title={t('letterAnalyse.errorsSection')}>
              <Text style={styles.faults}>{t('letterAnalyse.faults', { count: analyse.nombre_fautes })}</Text>
              {analyse.erreurs_detectees.map((e) => (
                <View key={e.numero} style={styles.erreur} testID={`letter-analyse-error-${e.numero}`}>
                  <View style={styles.erreurHead}>
                    <Text style={styles.erreurTitle}>{e.erreur}</Text>
                    <Text style={[styles.gravite, { color: GRAVITE_TON[e.gravite] ?? colors.grey500 }]}>
                      {t(`letterAnalyse.gravites.${e.gravite}`)}
                    </Text>
                  </View>
                  {e.extrait ? <Text style={styles.extrait}>« {e.extrait} »</Text> : null}
                  <Text style={styles.erreurText}>{e.explication_pedagogique}</Text>
                  {e.correction_suggeree ? <Text style={styles.correction}>{e.correction_suggeree}</Text> : null}
                </View>
              ))}
            </Section>
          ) : null}

              </>
  );
  const rIncoh = (
    <>
{analyse.incoherences_avec_cv.length > 0 ? (
            <Section title={t('letterAnalyse.incoherenceSection')}>
              {analyse.incoherences_avec_cv.map((inc, i) => (
                <View key={i} testID={`letter-analyse-incoherence-${i}`} style={styles.erreur}>
                  <Text style={styles.erreurTitle}>{inc.affirmation}</Text>
                  <Text style={styles.erreurText}>{inc.constat}</Text>
                </View>
              ))}
            </Section>
          ) : null}
    </>
  );
  const rPied = (
    <>
      <PiedAction testID="letter-analyse-result-actions" enColonne={large}>
        {onImprove && lettreAnalysee ? (
          <>
            <GradientButton
              variante="cta"
              hauteur="lg"
              label={t('letterAnalyse.improve')}
              onPress={() =>
                onImprove({
                  letter: lettreAnalysee,
                  diagnostic: obsolete ? undefined : diagnosticPourAmelioration(analyse),
                  annonce: annonce.trim(),
                  cv: cvPrincipal ? JSON.stringify(cvPrincipal.content) : '',
                  // Le profil de l'analyse affichée, pas celui du formulaire : rouvert plus tard, le formulaire a son défaut.
                  profil: (LETTER_PROFILS as readonly string[]).includes(analyse.profil) ? (analyse.profil as LetterProfil) : profil,
                })
              }
              testID="letter-analyse-improve"
            />
            <Button title={t('letterAnalyse.reanalyse')} variant="secondary" onPress={() => setEtape('saisie')} testID="letter-analyse-again" />
          </>
        ) : (
          <GradientButton
            variante="cta"
            hauteur="lg"
            label={t('letterAnalyse.reanalyse')}
            onPress={() => setEtape('saisie')}
            testID="letter-analyse-again"
          />
        )}
      </PiedAction>
    </>
  );

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <View testID="letter-analyse-header">
        <ScreenHeader variante="carte" onBack={onBack} testID="letter-analyse-back" title={t('letterAnalyse.resultTitre')} />
      </View>

      {/* Ordinateur : le score et les actions à gauche, le détail à droite. Un résultat peut défiler. */}
      <ScrollView contentContainerStyle={[styles.contenuSaisie, large && styles.contenuLargeAn]}>
        <View testID="letter-analyse-result" style={[styles.resultat, large && styles.resultatLarge]}>
          {large ? (
            <>
              <View style={styles.colGaucheAn}>
                {rTete}
                {rPrior}
                {rForts}
                <View style={styles.piedAn}>{rPied}</View>
              </View>
              <View style={styles.colGaucheAn}>
                {rDetail}
                {rErreurs}
                {rIncoh}
              </View>
            </>
          ) : (
            <>
              {rTete}
              {rPrior}
              {rForts}
              {rDetail}
              {rErreurs}
              {rIncoh}
            </>
          )}
        </View>
      </ScrollView>

      {large ? null : rPied}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  colonnesAnalyse: {
    flexGrow: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.xl,
    width: '100%',
    maxWidth: layout.pageMaxWidth,
    alignSelf: 'center',
    padding: spacing.xxl + spacing.xl,
  },
  colGaucheAn: { flex: 1, minWidth: 0, gap: spacing.md },
  piedAn: { marginTop: spacing.sm },
  contenuLargeAn: { width: '100%', maxWidth: layout.pageMaxWidth, alignSelf: 'center', padding: spacing.xxl + spacing.xl },
  resultatLarge: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.xl },
  container: { flex: 1, backgroundColor: colors.grey50 },

  loading: { fontSize: typography.body.size, color: colors.grey500, textAlign: 'center' },

  warning: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: radius.md,
    backgroundColor: colors.warningSoft,
  },
  warningText: { flex: 1, fontSize: typography.small.size, color: colors.grey700 },

  cvNote: { fontSize: typography.caption.size, color: colors.grey500, marginTop: spacing.sm },

  section: { gap: spacing.sm, marginTop: spacing.sm },
  sectionTitle: {
    fontSize: typography.h3.size,
    lineHeight: typography.h3.lineHeight,
    fontWeight: typography.h3.weight,
    color: colors.night,
  },

  resultat: { gap: spacing.md },
  attente: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: spacing.md, padding: screenPadding },

  // Ligne de priorité ou de point fort : une carte blanche, une pastille à gauche.
  priorite: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: formes.rayon.md,
    backgroundColor: colors.white,
    ...shadow.card,
  },
  fort: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: formes.rayon.md,
    backgroundColor: colors.white,
    ...shadow.card,
  },
  numero: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ACCENT.tint,
  },
  numeroTexte: { fontSize: 13, fontWeight: '800', color: ACCENT.ink },
  pastilleFort: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.success,
  },
  // `flex: 1` + `minWidth: 0` : un texte long passe à la ligne au lieu de sortir de la carte.
  prioriteTexte: { flex: 1, minWidth: 0, fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey700 },

  critere: { gap: 2, paddingVertical: spacing.xs },
  critereHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  critereName: { fontSize: typography.small.size, fontWeight: '700', color: colors.night },
  critereNiveau: { fontSize: typography.caption.size, fontWeight: '700', color: colors.grey500 },
  critereJustif: { fontSize: typography.caption.size, color: colors.grey500 },

  faults: { fontSize: typography.small.size, color: colors.grey700 },
  erreur: {
    gap: 2,
    padding: spacing.md,
    borderRadius: radius.md,
    backgroundColor: colors.white,
    ...shadow.card,
  },
  erreurHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm },
  erreurTitle: { flex: 1, fontSize: typography.small.size, fontWeight: '700', color: colors.night },
  gravite: { fontSize: typography.caption.size, fontWeight: '700' },
  extrait: { fontSize: typography.caption.size, fontStyle: 'italic', color: colors.grey500 },
  erreurText: { fontSize: typography.caption.size, lineHeight: 16, color: colors.grey700 },
  correction: { fontSize: typography.caption.size, fontWeight: '700', color: colors.electric },

  field: { gap: spacing.xs },
  fieldLabel: { fontSize: typography.small.size, fontWeight: '700', color: colors.grey700 },
  fieldHint: { fontSize: typography.caption.size, color: colors.grey500 },

  flex: { flex: 1 },
  contenuSaisie: { padding: screenPadding, paddingBottom: spacing.xxl, gap: spacing.md },
  intro: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight + 2, color: colors.grey500 },
  bloc: { gap: spacing.sm },

  // Champs : étiquette et saisie dans une même carte.
  champCarte: {
    gap: spacing.xs,
    padding: spacing.md,
    borderRadius: formes.rayon.md,
    backgroundColor: colors.white,
    ...shadow.card,
  },
  saisie: {
    minHeight: formes.hauteur.sm,
    fontSize: typography.body.size,
    fontWeight: '500',
    color: colors.night,
  },
  saisieLongue: { minHeight: 120, textAlignVertical: 'top' },

  // « Mes lettres » : une ligne par lettre, avec une pastille de sélection.
  liste: { gap: spacing.sm },
  ligneLettre: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    minHeight: formes.hauteur.lg,
    padding: spacing.md,
    borderRadius: formes.rayon.md,
    backgroundColor: colors.white,
    borderWidth: 1.5,
    borderColor: 'transparent',
    ...shadow.card,
  },
  ligneLettreActive: { borderColor: ACCENT.ink },
  vignette: {
    width: 36,
    height: 44,
    borderRadius: formes.rayon.sm / 2,
    backgroundColor: ACCENT.tint,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ligneTextes: { flex: 1, minWidth: 0, gap: 1 },
  ligneTitre: { fontSize: typography.bodyMd.size, fontWeight: '800', color: colors.night },
  ligneSous: { fontSize: typography.caption.size + 1, color: colors.grey500 },
  radio: {
    width: 22,
    height: 22,
    borderRadius: radius.pill,
    borderWidth: 2,
    borderColor: colors.grey400,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Fond en encre : le blanc sur l'accent clair du magenta ne tient pas 4,5:1.
  radioOn: { backgroundColor: ACCENT.ink, borderColor: ACCENT.ink },

  // « Importer » : une grande zone de dépôt en pointillés.
  depot: {
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.lg,
    borderRadius: formes.rayon.lg,
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: ACCENT.color,
    backgroundColor: colors.white,
  },
  depotIcone: {
    width: 46,
    height: 46,
    borderRadius: formes.rayon.sm,
    backgroundColor: ACCENT.tint,
    alignItems: 'center',
    justifyContent: 'center',
  },
  depotTitre: { fontSize: typography.bodyMd.size, fontWeight: '800', color: colors.night },
  depotSous: { fontSize: typography.small.size, color: colors.grey500, textAlign: 'center' },
  importPicked: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm },
  importPickedText: { flex: 1, fontSize: typography.small.size, color: colors.grey700 },
  importRemove: { fontSize: typography.small.size, fontWeight: '800', color: ACCENT.ink },

  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  chip: {
    minHeight: formes.hauteur.sm,
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
    borderRadius: radius.pill,
    backgroundColor: colors.white,
    borderWidth: 1.5,
    borderColor: colors.grey200,
  },
  chipActive: { backgroundColor: ACCENT.ink, borderColor: ACCENT.ink },
  chipText: { fontSize: typography.small.size, color: colors.grey700 },
  chipTextActive: { color: colors.white, fontWeight: '800' },

  infoCv: { fontSize: typography.small.size, color: colors.grey500 },
  erreurPied: { fontSize: typography.small.size, color: colors.error, textAlign: 'center' },
});
